// @ts-nocheck
// ═══ RESPONSES ════════════════════════════════════════════
// Ported verbatim from legacy/EMR Forms.html: the responses table + stat
// cards (buildResponses), the row Edit modal (openEditModal / saveEditModal,
// with its own per-type inline inputs), and the two exports —
// Google-Forms-style CSV (UTF-8 BOM, quoted cells, comma-space multi-selects)
// and native SpreadsheetML "Excel" .xls (purple #673AB7 header, alternating
// row tint, frozen header row).

import type { Ctx } from './engineTypes.ts';
import { esc } from './state.ts';
import { COUNTRIES, countryByIso, parsePhoneVal, fmtPhoneDisplay } from './countries.ts';
import { putResponse } from './api.ts';

export function attachResponses(ctx: Ctx): void {
  const doc = ctx.container;
  const state = ctx.state;
  const toast = ctx.toast;
  const $ = (id: string): any => doc.querySelector('#' + id);

  // Value readers for "Checkbox + Other" (mirrors runtime.ts — the legacy had
  // one shared copy in the single script scope).
  function readMultiOther(el: any) {
    const out = [...el.querySelectorAll('input[type=checkbox]:checked')].filter((cb: any) => !cb.dataset.other).map((cb: any) => cb.value);
    const ocb = el.querySelector('input[type=checkbox][data-other]');
    const oInp = el.querySelector('input[data-other-text]');
    if (ocb && ocb.checked && oInp && oInp.value.trim()) out.push(oInp.value.trim());
    return out;
  }
  function readSingleOther(el: any) {
    const orb = el.querySelector('input[type=radio][data-other]');
    const oInp = el.querySelector('input[data-other-text]');
    if (orb && orb.checked) return (oInp && oInp.value.trim()) || '';
    const checked = el.querySelector('input[type=radio]:checked');
    return checked ? checked.value : '';
  }

  function buildResponses() {
    const thead = doc.querySelector('#resp-table thead'), tbody = doc.querySelector('#resp-table tbody');
    thead.innerHTML = ''; tbody.innerHTML = '';
    const hrow = document.createElement('tr');
    const locHdr = state.captureLocation ? '<th>📍 Location</th>' : '';
    hrow.innerHTML = '<th style="width:32px"></th><th>ID</th><th>🕐 Form Start</th><th>🕑 Form End</th>' + locHdr + state.questions.map((q: any) => '<th>' + esc(q.label) + '</th>').join('');
    thead.appendChild(hrow);
    state.responses.forEach((r: any, ri: number) => {
      const row = document.createElement('tr'); row.className = 'resp-row-edit';
      const map = Object.fromEntries(r.answers.map((a: any) => [String(a.questionId), a.value]));
      const fmt = (v: any) => Array.isArray(v) ? v.join('; ') : (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? ''));
      const editTd = document.createElement('td'); editTd.style.cssText = 'text-align:center;padding:6px';
      const editBtn = document.createElement('button'); editBtn.type = 'button'; editBtn.className = 'btn sm ghost icon'; editBtn.title = 'Edit this response'; editBtn.textContent = '✎';
      editBtn.addEventListener('click', (e: any) => { e.stopPropagation(); openEditModal(ri); });
      editTd.appendChild(editBtn); row.appendChild(editTd);
      const cell = (q: any) => q.type === 'PHONE' ? fmtPhoneDisplay(map[q.id]) : fmt(map[q.id]);
      const locCell = state.captureLocation ? `<td>${r.respondentLocation ? esc(r.respondentLocation.lat + ', ' + r.respondentLocation.lng + ' (±' + r.respondentLocation.accuracy + 'm)') : '<span style="color:var(--txt3)">—</span>'}</td>` : '';
      const startTime = r.fillingStartedAt ? new Date(r.fillingStartedAt).toLocaleString() : '<span style="color:var(--txt3)">—</span>';
      const endTime = r.fillingEndedAt ? new Date(r.fillingEndedAt).toLocaleString() : '<span style="color:var(--txt3)">—</span>';
      row.insertAdjacentHTML('beforeend', `<td>${r.id}</td><td>${startTime}</td><td>${endTime}</td>` + locCell + state.questions.map((q: any) => `<td>${esc(cell(q))}</td>`).join(''));
      row.addEventListener('click', () => openEditModal(ri));
      tbody.appendChild(row);
    });
    const n = state.responses.length;
    $('resp-sub').textContent = n + ' submission' + (n !== 1 ? 's' : '');
    $('stat-total').textContent = n;
    $('stat-qs').textContent = state.questions.length;
  }

  // ── Edit Modal ───────────────────────────────────────────
  let editingRespIdx: number | null = null;
  function openEditModal(ri: number) {
    editingRespIdx = ri;
    const r = state.responses[ri];
    const map = Object.fromEntries(r.answers.map((a: any) => [String(a.questionId), a.value]));
    const body = $('edit-modal-body'); body.innerHTML = '';
    const meta = document.createElement('div'); meta.style.cssText = 'font-size:11px;color:var(--txt3);margin-bottom:4px';
    meta.textContent = 'Response ID: ' + r.id + ' • ' + new Date(r.createdAt).toLocaleString();
    body.appendChild(meta);
    state.questions.forEach((q: any) => {
      if (q.type === 'NOTE') return;
      const wrap = document.createElement('div'); wrap.className = 'modal-field';
      const lbl = document.createElement('label'); lbl.textContent = q.label + (q.required ? ' *' : ''); wrap.appendChild(lbl);
      const cur = map[q.id];
      let inp: any = null;
      if (q.type === 'LONG_TEXT') { inp = document.createElement('textarea'); inp.rows = 3; inp.value = cur ?? ''; }
      else if (q.type === 'SINGLE_CHOICE' || q.type === 'DROPDOWN') {
        inp = document.createElement('select');
        const blank = document.createElement('option'); blank.value = ''; blank.textContent = '— Select —'; inp.appendChild(blank);
        (q.meta?.choices || []).forEach((c: any) => { const o = document.createElement('option'); o.value = c.value; o.textContent = c.label; if (cur === c.value) o.selected = true; inp.appendChild(o); });
      } else if (q.type === 'MULTI_SELECT') {
        inp = document.createElement('div'); inp.style.cssText = 'display:grid;gap:5px';
        const sel = new Set(Array.isArray(cur) ? cur : []);
        (q.meta?.choices || []).forEach((c: any) => { const l = document.createElement('label'); l.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer'; const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = c.value; cb.checked = sel.has(c.value); l.append(cb, c.label); inp.appendChild(l); });
      } else if (q.type === 'CHECKBOX_OTHER') {
        const _mode = (q.meta?.mode || 'multi');
        const _otherLbl = ((q.meta?.otherLabel || '').trim() || 'Other') + ':';
        inp = document.createElement('div'); inp.style.cssText = 'display:grid;gap:5px';
        const cvals = (q.meta?.choices || []).map((c: any) => c.value);
        if (_mode === 'single') {
          inp.dataset.type = 'SINGLEOTHER';
          const sv = (cur == null) ? '' : String(cur);
          const isOther = sv !== '' && !cvals.includes(sv);
          (q.meta?.choices || []).forEach((c: any) => { const l = document.createElement('label'); l.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer'; const rb = document.createElement('input'); rb.type = 'radio'; rb.name = 'em_' + q.id; rb.value = c.value; if (sv === c.value) rb.checked = true; l.append(rb, c.label); inp.appendChild(l); });
          const ol = document.createElement('label'); ol.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer';
          const orb = document.createElement('input'); orb.type = 'radio'; orb.name = 'em_' + q.id; orb.dataset.other = '1'; orb.checked = isOther;
          const otxt = document.createElement('input'); otxt.type = 'text'; otxt.dataset.otherText = '1'; otxt.placeholder = 'Other…'; otxt.value = isOther ? sv : ''; otxt.style.flex = '1';
          ol.append(orb, document.createTextNode(_otherLbl), otxt); inp.appendChild(ol);
        } else {
          inp.dataset.type = 'MULTIOTHER';
          const arr = Array.isArray(cur) ? cur : [];
          const otherTxt = arr.find((v: any) => !cvals.includes(v));
          const sel = new Set(arr);
          (q.meta?.choices || []).forEach((c: any) => { const l = document.createElement('label'); l.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer'; const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = c.value; cb.checked = sel.has(c.value); l.append(cb, c.label); inp.appendChild(l); });
          const ol = document.createElement('label'); ol.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer';
          const ocb = document.createElement('input'); ocb.type = 'checkbox'; ocb.dataset.other = '1'; ocb.checked = otherTxt != null;
          const otxt = document.createElement('input'); otxt.type = 'text'; otxt.dataset.otherText = '1'; otxt.placeholder = 'Other…'; otxt.value = otherTxt || ''; otxt.style.flex = '1';
          ol.append(ocb, document.createTextNode(_otherLbl), otxt); inp.appendChild(ol);
        }
      } else if (q.type === 'DATE') { inp = document.createElement('input'); inp.type = 'date'; inp.value = cur ?? ''; }
      else if (q.type === 'TIME') { inp = document.createElement('input'); inp.type = 'time'; inp.value = cur ?? ''; }
      else if (q.type === 'DATETIME') { inp = document.createElement('input'); inp.type = 'datetime-local'; inp.value = cur ?? ''; }
      else if (q.type === 'NUMBER') { inp = document.createElement('input'); inp.type = 'number'; inp.step = '1'; inp.value = cur ?? ''; }
      else if (q.type === 'DECIMAL') { inp = document.createElement('input'); inp.type = 'number'; inp.step = 'any'; inp.value = cur ?? ''; }
      else if (q.type === 'EMAIL') { inp = document.createElement('input'); inp.type = 'email'; inp.value = cur ?? ''; }
      else if (q.type === 'PHONE') {
        inp = document.createElement('div'); inp.className = 'rt-phone'; inp.dataset.type = 'PHONE';
        const prow = document.createElement('div'); prow.className = 'rt-phone-row';
        const csel = document.createElement('select'); csel.className = 'rt-phone-cc';
        const p = parsePhoneVal(cur);
        COUNTRIES.forEach((c: any) => { const o = document.createElement('option'); o.value = c.iso; o.textContent = c.flag + ' +' + c.dial; if (c.iso === p.iso) o.selected = true; csel.appendChild(o); });
        const num = document.createElement('input'); num.type = 'tel'; num.className = 'rt-phone-num'; num.value = p.national; num.placeholder = 'Phone number';
        const sync = () => { const cc = countryByIso(csel.value); inp.dataset.val = JSON.stringify({ iso: csel.value, dial: cc.dial, national: num.value.replace(/\D/g, ''), e164: '+' + cc.dial + num.value.replace(/\D/g, '') }); };
        csel.addEventListener('change', sync); num.addEventListener('input', sync); sync();
        prow.append(csel, num); inp.appendChild(prow);
      }
      else if (q.type === 'RATING') {
        inp = document.createElement('div'); inp.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap';
        let sel = cur || null;
        for (let i = 1; i <= (q.meta?.max || 10); i++) { const b = document.createElement('button'); b.type = 'button'; b.className = 'rating-btn'; b.textContent = String(i); if (sel == i) b.classList.add('active'); b.addEventListener('click', () => { inp.querySelectorAll('.rating-btn').forEach((x: any) => x.classList.remove('active')); b.classList.add('active'); sel = i; inp.dataset.val = String(i); }); inp.appendChild(b); }
        inp.dataset.val = sel || '';
      } else if (q.type === 'ACKNOWLEDGE') {
        inp = document.createElement('label'); inp.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer';
        const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = cur === 'yes'; inp.append(cb, 'I acknowledge');
      } else {
        inp = document.createElement('input'); inp.type = 'text'; inp.value = cur ?? '';
      }
      if (inp) { inp.dataset.qid = q.id; wrap.appendChild(inp); }
      body.appendChild(wrap);
    });
    $('edit-modal-overlay').classList.remove('hide');
  }
  function closeEditModal() { $('edit-modal-overlay').classList.add('hide'); editingRespIdx = null; }
  function saveEditModal() {
    if (editingRespIdx === null) return;
    const r = state.responses[editingRespIdx];
    const body = $('edit-modal-body');
    r.answers = state.questions.map((q: any) => {
      const el = body.querySelector(`[data-qid="${q.id}"]`);
      let val: any = null;
      if (!el) { const old = r.answers.find((a: any) => String(a.questionId) === String(q.id)); val = old ? old.value : null; }
      else if (q.type === 'MULTI_SELECT') { val = [...el.querySelectorAll('input[type=checkbox]:checked')].map((c: any) => c.value); }
      else if (q.type === 'CHECKBOX_OTHER') { val = ((q.meta?.mode || 'multi') === 'single') ? readSingleOther(el) : readMultiOther(el); }
      else if (q.type === 'RATING') { val = el.dataset.val || ''; }
      else if (q.type === 'PHONE') { val = el.dataset.val || ''; }
      else if (q.type === 'ACKNOWLEDGE') { val = el.querySelector('input[type=checkbox]')?.checked ? 'yes' : ''; }
      else { val = el.value ?? ''; }
      return { questionId: q.id, value: val };
    });
    // Persist updated response to server. Legacy: fetch(PUT).then(if r.ok
    // setServerStatus(true)).catch(setServerStatus(false)).
    putResponse(ctx.formId, r.id, r).then(() => ctx.setServerStatus(true)).catch(() => ctx.setServerStatus(false));
    buildResponses(); closeEditModal(); toast('Response updated!', 'ok');
  }
  $('edit-modal-close').addEventListener('click', closeEditModal);
  $('edit-modal-cancel').addEventListener('click', closeEditModal);
  $('edit-modal-save').addEventListener('click', saveEditModal);
  $('edit-modal-overlay').addEventListener('click', (e: any) => { if (e.target === $('edit-modal-overlay')) closeEditModal(); });

  // ── Shared export helpers ────────────────────────────────
  function gTimestamp(iso: any) {
    const d = new Date(iso), p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }
  function flatVal(v: any) {
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'object' && v !== null) return JSON.stringify(v);
    return String(v ?? '');
  }
  const cellVal = (q: any, v: any) => q.type === 'PHONE' ? fmtPhoneDisplay(v) : flatVal(v);

  // ── CSV Export (Google Forms style) ──────────────────────
  $('downloadCsv').onclick = () => {
    if (!state.responses.length) { toast('No responses yet.', 'info'); return; }
    const csvCell = (v: any) => '"' + String(v).replace(/"/g, '""') + '"';
    const locHeaders = state.captureLocation ? ['Latitude', 'Longitude', 'Location Accuracy (m)'] : [];
    const headers = ['Form Start', 'Form End', ...locHeaders, ...state.questions.map((q: any) => q.label)];
    const lines = [headers.map(csvCell).join(',')];
    state.responses.forEach((r: any) => {
      const map = Object.fromEntries(r.answers.map((a: any) => [String(a.questionId), a.value]));
      const loc = r.respondentLocation;
      const locCells = state.captureLocation ? [loc ? String(loc.lat) : '', loc ? String(loc.lng) : '', loc ? String(loc.accuracy) : ''] : [];
      lines.push([r.fillingStartedAt ? gTimestamp(r.fillingStartedAt) : '', r.fillingEndedAt ? gTimestamp(r.fillingEndedAt) : gTimestamp(r.createdAt), ...locCells, ...state.questions.map((q: any) => cellVal(q, map[q.id]))].map(csvCell).join(','));
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    a.download = (state.title || 'Form Responses') + ' (Responses).csv'; a.click();
    toast('CSV exported!', 'ok');
  };

  // ── Excel Export (Google Forms style, native SpreadsheetML) ──
  $('downloadXlsx').onclick = () => {
    if (!state.responses.length) { toast('No responses yet.', 'info'); return; }
    const xe = (v: any) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const cell = (v: any, sid: any) => `<Cell${sid ? ` ss:StyleID="${sid}"` : ''}><Data ss:Type="String">${xe(v)}</Data></Cell>`;
    const styles = `<Styles>
    <Style ss:ID="H"><Font ss:Bold="1" ss:Color="#FFFFFF" ss:Size="10" ss:FontName="Arial"/><Interior ss:Color="#673AB7" ss:Pattern="Solid"/><Alignment ss:Horizontal="Left" ss:Vertical="Center"/></Style>
    <Style ss:ID="D"><Font ss:Size="10" ss:FontName="Arial"/><Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/></Style>
    <Style ss:ID="Z"><Font ss:Size="10" ss:FontName="Arial"/><Interior ss:Color="#F3E5F5" ss:Pattern="Solid"/><Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/></Style>
  </Styles>`;
    const xlLocHeaders = state.captureLocation ? ['Latitude', 'Longitude', 'Location Accuracy (m)'] : [];
    const xlLocCols = state.captureLocation ? '<Column ss:Width="120"/><Column ss:Width="120"/><Column ss:Width="120"/>' : '';
    const colDefs = `<Column ss:Width="160"/><Column ss:Width="160"/>${xlLocCols}${state.questions.map(() => '<Column ss:Width="200"/>').join('')}`;
    const hdrRow = `<Row ss:Height="22">${['Form Start', 'Form End', ...xlLocHeaders, ...state.questions.map((q: any) => q.label)].map((v: any) => cell(v, 'H')).join('')}</Row>`;
    const dataRows = state.responses.map((r: any, i: number) => {
      const map = Object.fromEntries(r.answers.map((a: any) => [String(a.questionId), a.value]));
      const loc = r.respondentLocation;
      const locCells = state.captureLocation ? [loc ? String(loc.lat) : '', loc ? String(loc.lng) : '', loc ? String(loc.accuracy) : ''] : [];
      const style = i % 2 === 0 ? 'D' : 'Z';
      return `<Row ss:Height="18">${[r.fillingStartedAt ? gTimestamp(r.fillingStartedAt) : '', r.fillingEndedAt ? gTimestamp(r.fillingEndedAt) : gTimestamp(r.createdAt), ...locCells, ...state.questions.map((q: any) => cellVal(q, map[q.id]))].map((v: any) => cell(v, style)).join('')}</Row>`;
    }).join('');
    const xml = `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet" xmlns:x="urn:schemas-microsoft-com:office:excel">
<DocumentProperties xmlns="urn:schemas-microsoft-com:office:office"><Title>${xe(state.title || 'Form Responses')}</Title></DocumentProperties>
${styles}
<Worksheet ss:Name="Form Responses 1"><Table>${colDefs}${hdrRow}${dataRows}</Table>
<WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel"><FreezePanes/><FrozenNoSplit/><SplitHorizontal>1</SplitHorizontal><TopRowBottomPane>1</TopRowBottomPane></WorksheetOptions>
</Worksheet></Workbook>`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' }));
    a.download = (state.title || 'Form Responses') + ' (Responses).xls'; a.click();
    toast('Excel exported!', 'ok');
  };

  ctx.buildResponses = buildResponses;
  ctx.openEditModal = openEditModal;
}
